import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormsModule } from '@angular/forms';
import { AccountSession, AccountItem, AccountItemsResponse } from '../account.type';
import { AccountService } from '../account.service';
import { MatDividerModule } from '@angular/material/divider';

export interface AccountPlayersDialogData {
  account: AccountSession;
}

interface PlayerGroup {
  key: string;
  asset_id: number;
  player_name: string;
  rating: number;
  position: string;
  buy_now_price?: number;
  starting_bid?: number;
  location: "tradepile" | "unassigned";
  trade_state?: string;
  items: AccountItem[];
  count: number;
  discard_value: number;
  // For list player form
  listBuyNowPrice?: number;
  listStartingBid?: number;
  listingInProgress?: boolean;
  quickSellingInProgress?: boolean;
}

@Component({
  selector: 'app-account-players-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatDividerModule,
    FormsModule,
  ],
  templateUrl: './account-players-dialog.html',
  styleUrl: './account-players-dialog.scss'
})
export class AccountPlayersDialog implements OnInit {
  loading = true;
  credits = 0;
  playerGroups: PlayerGroup[] = [];

  constructor(
    private dialogRef: MatDialogRef<AccountPlayersDialog>,
    @Inject(MAT_DIALOG_DATA) public data: AccountPlayersDialogData,
    private accountService: AccountService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.loadPlayers();
  }

  loadPlayers(): void {
    this.loading = true;
    this.accountService.getAccountItems(this.data.account.sid).subscribe({
      next: (response: AccountItemsResponse) => {
        this.credits = response.credits;
        this.playerGroups = this.groupPlayers(response.items);
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading players:', err);
        this.snackBar.open('Error loading players', 'Close', { duration: 3000 });
        this.loading = false;
      }
    });
  }

  groupPlayers(items: AccountItem[]): PlayerGroup[] {
    const groupMap = new Map<string, PlayerGroup>();

    if (!items || items.length === 0) {
      return [];
    }

    items.forEach(item => {
      const key = `${item.asset_id}-${item.buy_now_price || 0}-${item.starting_bid || 0}-${item.location}-${item.trade_state || 'none'}`;

      if (groupMap.has(key)) {
        const group = groupMap.get(key)!;
        group.items.push(item);
        group.count++;
      } else {
        groupMap.set(key, {
          key,
          asset_id: item.asset_id,
          player_name: item.player_name,
          rating: item.rating,
          position: item.position,
          buy_now_price: item.buy_now_price,
          discard_value: item.discard_value,
          starting_bid: item.starting_bid,
          location: item.location,
          trade_state: item.trade_state,
          items: [item],
          count: 1
        });
      }
    });

    return Array.from(groupMap.values()).sort((a, b) => {
      // Sort by location (unassigned first), then by rating, then by player name
      if (a.location !== b.location) {
        return a.location === 'unassigned' ? -1 : 1;
      }
      if (a.rating !== b.rating) {
        return b.rating - a.rating;
      }
      return a.player_name.localeCompare(b.player_name);
    });
  }

  listPlayer(group: PlayerGroup): void {
    if (!group.listBuyNowPrice || !group.listStartingBid) {
      this.snackBar.open('Please enter both buy now price and starting bid', 'Close', { duration: 3000 });
      return;
    }

    if (group.listStartingBid >= group.listBuyNowPrice) {
      this.snackBar.open('Starting bid must be less than buy now price', 'Close', { duration: 3000 });
      return;
    }

    // List the first item in the group
    const itemToList = group.items[0];
    group.listingInProgress = true;

    this.accountService.listPlayer(
      this.data.account.sid,
      {
        item_id: itemToList.id,
        buy_now_price: group.listBuyNowPrice,
        starting_bid: group.listStartingBid,
        duration: 3600,
      }
    ).subscribe({
      next: (response) => {
        this.snackBar.open(response.message || 'Player listed successfully', 'Close', { duration: 3000 });
        group.listingInProgress = false;
        // Reload players to get updated data
        this.loadPlayers();
      },
      error: (err) => {
        console.error('Error listing player:', err);
        const errorMessage = err.error?.error || 'Failed to list player';
        this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
        group.listingInProgress = false;
      }
    });
  }

  quickSellPlayer(group: PlayerGroup): void {
    // List the first item in the group
    const itemToQuickSell = group.items[0];
    group.quickSellingInProgress = true;

    this.accountService.quickSellPlayer(
      this.data.account.sid,
      {
        item_id: itemToQuickSell.id
      }
    ).subscribe({
      next: (response) => {
        this.snackBar.open(response.message || 'Player quick sold successfully', 'Close', { duration: 3000 });
        group.quickSellingInProgress = false;
        // Reload players to get updated data
        this.loadPlayers();
      },
      error: (err) => {
        console.error('Error quick selling player:', err);
        const errorMessage = err.error?.error || 'Failed to quick sell player';
        this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
        group.quickSellingInProgress = false;
      }
    });
  }

  formatExpires(seconds: number): string {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }

  getLocationIcon(location: string): string {
    return location === 'tradepile' ? 'storefront' : 'inventory_2';
  }

  getGroupColor(group: PlayerGroup): string {
    if (group.location === 'unassigned') {
      return 'not-active-group';
    }

    switch (group.trade_state) {
      case 'active':
        return 'active-group';
      case 'closed':
        return 'closed-group';
      default:
        return 'not-active-group';
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  getStatusName(group: PlayerGroup): string {
    if (group.location === 'unassigned') {
      return 'Unassigned';
    }

    switch (group.trade_state) {
      case 'active':
        return 'Active Transfer';
      case 'expired':
        return 'Expired (Unsold)';
      case 'closed':
        return 'Sold';
      default:
        return 'Available';
    }
  }
}
